/**
 * Array-safe weekly meal distribution (Step 3 duplicate-category compatibility).
 *
 * Every category holds a LIST, so a single day can legitimately carry two
 * Lunches / two Dinners / multiple Snacks. No selected/pinned/existing row is
 * ever dropped, and each real row is preserved with its own native meal_type.
 *
 * Returns: { [date]: { breakfast:[], lunch:[], dinner:[], snack:[] } }
 * Existing rows are tagged { existing: true, ...row } so callers can skip
 * re-persisting them.
 *
 * ES module: imported by the React app via `import`, and by the node-based
 * unit test via a dynamic `import()`.
 */
const CATEGORIES = ['breakfast', 'lunch', 'dinner', 'snack'];

function _dayCalories(dayMeals) {
  let c = 0;
  CATEGORIES.forEach(cat => (dayMeals[cat] || []).forEach(m => {
    c += m.existing ? ((m.macros && m.macros.calories) || m.calories || 0) : (m.calories || 0);
  }));
  return c;
}

function distributeWeeklyPlan(weekDates, selectedMeals, pinnedMeals, existingPlans) {
  const plan = {};
  weekDates.forEach(date => {
    plan[date] = { breakfast: [], lunch: [], dinner: [], snack: [] };
  });

  // Existing plans coexist with new rows and are never overwritten.
  for (const ex of (existingPlans || [])) {
    const date = ex.date;
    const mealType = (ex.meal_type || '').toLowerCase();
    if (plan[date] && plan[date][mealType]) {
      plan[date][mealType].push({ existing: true, ...ex });
    }
  }

  // Pinned meals go to their pinned date (append — never replace).
  Object.entries(pinnedMeals || {}).forEach(([mealId, date]) => {
    for (const [category, meals] of Object.entries(selectedMeals || {})) {
      const meal = (meals || []).find(m => m.meal_id === mealId);
      if (meal && plan[date] && plan[date][category]) {
        plan[date][category].push(meal);
      }
    }
  });

  // Distribute the rest: each meal to the day holding the FEWEST of its category
  // (preserves a one-per-day spread), tie-broken by lowest day calories. A
  // surplus same-category meal doubles up rather than being dropped.
  for (const [category, meals] of Object.entries(selectedMeals || {})) {
    if (!plan[weekDates[0]] || !(category in plan[weekDates[0]])) continue;
    const unpinned = (meals || []).filter(m => !(pinnedMeals && pinnedMeals[m.meal_id]));
    const sorted = [...unpinned].sort((a, b) => (b.calories || 0) - (a.calories || 0));
    for (const meal of sorted) {
      let bestDate = null, bestCount = Infinity, bestCal = Infinity;
      for (const date of weekDates) {
        const count = plan[date][category].length;
        const cal = _dayCalories(plan[date]);
        if (count < bestCount || (count === bestCount && cal < bestCal)) {
          bestCount = count; bestCal = cal; bestDate = date;
        }
      }
      if (bestDate) plan[bestDate][category].push(meal);
    }
  }

  return plan;
}

export { distributeWeeklyPlan, CATEGORIES };
